import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, RotateCcw, Package, Clock, MapPin, AlertCircle } from 'lucide-react';

const ShippingReturnsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-purple-700 text-white py-16 pt-32">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-purple-200 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <Truck className="w-10 h-10 text-amber-400" />
            <h1 className="text-4xl font-bold">Shipping & Returns</h1>
          </div>
          <p className="text-purple-200 mt-2">Delivery Information & Return Policy</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          
          {/* Shipping Section */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-purple-100 rounded-full">
                <Truck className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Shipping Information</h2>
            </div>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" /> Processing Time
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Orders are typically processed and shipped within <strong>1-2 business days</strong>. Orders placed on weekends or holidays will be processed the next business day. You will receive a shipping confirmation email with tracking information once your order has shipped.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-purple-600" /> Shipping Methods & Rates
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="text-left py-3 px-4 font-semibold text-purple-900 border">Method</th>
                      <th className="text-left py-3 px-4 font-semibold text-purple-900 border">Delivery Time</th>
                      <th className="text-left py-3 px-4 font-semibold text-purple-900 border">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-3 px-4 border">Standard Shipping</td>
                      <td className="py-3 px-4 border">5-7 Business Days</td>
                      <td className="py-3 px-4 border">$7.99</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="py-3 px-4 border">Expedited Shipping</td>
                      <td className="py-3 px-4 border">2-3 Business Days</td>
                      <td className="py-3 px-4 border">$14.99</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 border">Priority Overnight</td>
                      <td className="py-3 px-4 border">1 Business Day</td>
                      <td className="py-3 px-4 border">$29.99</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="py-3 px-4 border font-semibold text-purple-700">FREE Shipping</td>
                      <td className="py-3 px-4 border">5-7 Business Days</td>
                      <td className="py-3 px-4 border font-semibold text-green-600">Orders over $200</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Shipping Locations</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We currently ship to all 50 United States. Some products may have shipping restrictions to certain states based on local regulations. International shipping is not available at this time.
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-700 text-sm">
                    <strong>Note:</strong> We are unable to ship to P.O. Boxes for expedited or overnight shipping methods.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-purple-600" /> Packaging
              </h3>
              <p className="text-gray-600 leading-relaxed">
                All orders are shipped in discreet, unmarked packaging to protect your privacy. Peptides are carefully packaged in temperature-appropriate conditions to maintain product integrity. For temperature-sensitive products during summer months, cold packs may be included at no additional charge.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Order Tracking</h3>
              <p className="text-gray-600 leading-relaxed">
                Once your order ships, you will receive an email with tracking information. You can track your package through our website or directly through the carrier's website. If you have not received tracking information within 3 business days, please contact us.
              </p>
            </section>
          </div>

          {/* Divider */}
          <hr className="border-gray-200" />

          {/* Returns Section */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-purple-100 rounded-full">
                <RotateCcw className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Returns & Refunds</h2>
            </div>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Return Policy</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Due to the nature of our products (research peptides and compounds), we have specific return policies to ensure product integrity and safety:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Returns are accepted within <strong>14 days</strong> of delivery for unopened, unused products only</li>
                <li>Products must be in original, sealed packaging</li>
                <li>A Return Merchandise Authorization (RMA) number is required for all returns</li>
                <li>Customer is responsible for return shipping costs unless the return is due to our error</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Non-Returnable Items</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                The following items cannot be returned:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Opened or used products</li>
                <li>Products with broken seals</li>
                <li>Products that have been improperly stored</li>
                <li>Custom or special-order items</li>
                <li>Products beyond the 14-day return window</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Damaged or Defective Products</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you receive a damaged or defective product:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Contact us within <strong>48 hours</strong> of delivery</li>
                <li>Provide photos of the damage and packaging</li>
                <li>Do not discard the product or packaging until instructed</li>
                <li>We will ship a replacement at no charge or issue a full refund</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">How to Request a Return</h3>
              <ol className="list-decimal list-inside text-gray-600 space-y-2 ml-4">
                <li>Contact us at support@amino-chain.com with your order number</li>
                <li>Explain the reason for your return</li>
                <li>Wait for RMA approval and return instructions</li>
                <li>Ship the product back using the provided instructions</li>
                <li>Refund will be processed within 5-7 business days of receiving the return</li>
              </ol>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Refund Methods</h3>
              <p className="text-gray-600 leading-relaxed">
                Refunds will be issued to the original payment method. Please allow 5-10 business days for the refund to appear on your statement, depending on your financial institution.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Order Cancellations</h3>
              <p className="text-gray-600 leading-relaxed">
                Orders may be cancelled within 1 hour of placement for a full refund. After this window, orders enter processing and cannot be cancelled. If your order has already shipped, you will need to follow our return procedure.
              </p>
            </section>
          </div>

          {/* Contact */}
          <section className="pt-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions?</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions about shipping or returns, please don't hesitate to contact us:
            </p>
            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
              <p className="font-semibold text-purple-900">AminoChain Customer Support</p>
              <p className="text-purple-700">Email: support@amino-chain.com</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default ShippingReturnsPage;
